import React, { useCallback, useEffect, useRef, useState } from "react";
import { getWithExpiry } from "../../../Domain/Helpers/LocalStorageHelper";
import { getTransactionFilters } from "../../../Infrastructure/Services/TransactionService";
import { toast } from "react-toastify";
import UILabelInput from "../../../Domain/Components/UI/LabelInput/LabelInput";
import { inputType } from "../../../Domain/Constants/InputType";
import UIMultiselectModal from "../../../Domain/Components/UI/MultiSelectModal/MultiselectModal";
import UIButton from "../../../Domain/Components/UI/Button/Button";
import { styleType } from "../../../Domain/Constants/StyleType";
import { getChildrenList, hideDuplicates, prepareJsonArray } from "../../../Domain/Helpers/MultiselectHelper";
import usePagination from "../../../Infrastructure/Hooks/usePagination";
import UITable from "../../../Domain/Components/UI/Table/Table";
import { formatDate } from "../../../Domain/Helpers/DateHelper";
import UIGameConfigFilter from "../../../Domain/Components/UI/GameConfigFilter/UIGameConfigFilter";

const STORAGE_KEY = "transactionListState";

const loadSavedState = () => {
    try { return JSON.parse(sessionStorage.getItem(STORAGE_KEY)) || {}; } catch { return {}; }
};

function TransactionListPage(props) {
    const savedState = useRef(loadSavedState());

    const [filters, setFilters] = useState(null);
    const [casinos, setCasinos] = useState();
    const [agregators, setAgregators] = useState();
    const [providers, setProviders] = useState();
    const [page, setPage] = useState(1);
    const [formJsonBody, setFormJsonBody] = useState(null);
    const [rawFormValues, setRawFormValues] = useState(savedState.current.rawFormValues || {});
    const [sortChangedOnFirstPage, setSortChangedOnFirstPage] = useState(0);
    const [hasQualifyingFilter, setHasQualifyingFilter] = useState(savedState.current.hasQualifyingFilter || false);
    const [selectedCounts, setSelectedCounts] = useState(savedState.current.selectedCounts || {});
    const [savedGameConfigOptions, setSavedGameConfigOptions] = useState(savedState.current.savedGameConfigOptions || []);
    const [clearKey, setClearKey] = useState(0);

    const formRef = useRef(null);

    const { data, more, loading, error } = usePagination(
        page, 100, "transaction/get", null, sortChangedOnFirstPage,
        props.setLoggedIn, [], false, null,
        formJsonBody
    );

    // Save state to sessionStorage — formJsonBody intentionally excluded
    useEffect(() => {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
            rawFormValues,
            hasQualifyingFilter,
            selectedCounts,
            savedGameConfigOptions,
        }));
    }, [rawFormValues, hasQualifyingFilter, selectedCounts, savedGameConfigOptions]);

    // Re-check qualifying filters when rawFormValues are restored
    useEffect(() => {
        checkQualifyingFilters();
    }, [rawFormValues]);

    useEffect(() => {
        const token = getWithExpiry("token");
        if (token === null) {
            props.setLoggedIn(false);
            return;
        }
        getTransactionFilters(token).then((data) => {
            if (data) {
                setFilters(data);
            } else {
                toast.error("Failed to retreive transaction filter data");
                setFilters({});
            }
        });
    }, []);

    useEffect(() => {
        if (filters && filters.operators) {
            let tmpCasinos = [];
            filters.operators.forEach(o => { tmpCasinos = tmpCasinos.concat(o.casinos); });
            setCasinos(tmpCasinos);
        }
    }, [filters]);

    const onOperatorsSelected = () => {
        setTimeout(() => {
            let tmpCasinos = getChildrenList(filters.operators, "operators", "casinos");
            setCasinos(tmpCasinos);
        }, 1000);
    };

    useEffect(() => {
        if (casinos) {
            let tmpAgregators = getChildrenList(casinos, "casinos", "providers");
            setAgregators(tmpAgregators);
        }
    }, [casinos]);

    const onCasinosSelected = () => {
        setTimeout(() => {
            let tmpAgregators = getChildrenList(casinos, "casinos", "providers");
            setAgregators(tmpAgregators);
        }, 1000);
    };

    useEffect(() => {
        if (agregators) {
            let tmpProviders = getChildrenList(agregators, "agregators", "providers", true, "name");
            setProviders(tmpProviders);
        }
    }, [agregators]);

    const onAgregatorsSelected = () => {
        setTimeout(() => {
            let tmpProviders = getChildrenList(agregators, "agregators", "providers", true, "name");
            setProviders(tmpProviders);
        }, 1000);
    };

    useEffect(() => {
        if (providers) {
            getChildrenList(providers, "providers", "games");
        }
    }, [providers]);

    const onProvidersSelected = () => {
        setTimeout(() => {
            getChildrenList(providers, "providers", "games");
        }, 1000);
    };

    const checkQualifyingFilters = () => {
        const qualifyingFilters = ["id", "gameSessionId", "operatorSessionId", "providerTransactionId", "roundId"];
        
        if (formRef.current) {
            const formData = new FormData(formRef.current);
            const hasText = qualifyingFilters.some(key => {
                const val = formData.get(key);
                return val && val.trim() !== "";
            });
            if (hasText) {
                setHasQualifyingFilter(true);
                return;
            }
        }
    
        const hasText = qualifyingFilters.some(key => {
            const val = rawFormValues?.[key];
            return val && val.toString().trim() !== "";
        });
    
        setHasQualifyingFilter(hasText);
    };

    const onFilterFormSubmit = (e) => {
        e.preventDefault();
        let token = getWithExpiry('token');
        if (token === null) {
            props.setLoggedIn(false);
            return;
        }

        const form = e.target;
        const formData = new FormData(form);
        const rawJson = Object.fromEntries(formData.entries());
        const rawJsonCopy = { ...rawJson };

        const transactionType = prepareJsonArray({ ...rawJsonCopy }, "transactionType");
        const transactionStatus = prepareJsonArray({ ...rawJsonCopy }, "transactionStatus");
        const agregatorsVal = prepareJsonArray({ ...rawJsonCopy }, "agregators", true);
        const providersVal = prepareJsonArray({ ...rawJsonCopy }, "providers", true);
        const gameConfigsVal = prepareJsonArray({ ...rawJsonCopy }, "gameConfigs");

        const restrictedSelected =
            (transactionType && transactionType.length > 0) ||
            (transactionStatus && transactionStatus.length > 0) ||
            (agregatorsVal && agregatorsVal.length > 0) ||
            (providersVal && providersVal.length > 0) ||
            (gameConfigsVal && gameConfigsVal.length > 0);

        if (restrictedSelected) {
            const qualifyingFilters = ["id", "gameSessionId", "operatorSessionId", "providerTransactionId", "roundId"];
        
            const hasTextFilter = qualifyingFilters.some(key => {
                const val = formData.get(key);
                return val && val.trim() !== "";
            });
        
            if (!hasTextFilter) {
                alert("Please select at least one ID field when using Transaction Type, Status, Aggregator, Vendor, or Game Config.");
                return;
            }
        }

        setRawFormValues(rawJson);
        if (page !== 1) setPage(1);
        let formJson = prepareJsonForSending(formData);
        setFormJsonBody(formJson);
    };

    const onClearFilters = () => {
        setClearKey(prev => prev + 1);
        setFormJsonBody(null);
        setRawFormValues({});
        setHasQualifyingFilter(false);
        setSelectedCounts({});
        setSavedGameConfigOptions([]);
        savedState.current = {};
        sessionStorage.removeItem(STORAGE_KEY);
    };

    const prepareJsonForSending = (formData) => {
        let formJson = Object.fromEntries(formData.entries());
        formJson.columnName = "id";
        for (var key in formJson) {
            if (formJson[key] === "") delete formJson[key];
        }
        formJson.transactionType = prepareJsonArray(formJson, "transactionType");
        formJson.transactionStatus = prepareJsonArray(formJson, "transactionStatus");
        formJson.gameConfigs = prepareJsonArray(formJson, "gameConfigs");
        formJson.operators = prepareJsonArray(formJson, "operators", true);
        formJson.casinos = prepareJsonArray(formJson, "casinos", true);
        formJson.providers = prepareJsonArray(formJson, "providers", true);
        formJson.currencies = prepareJsonArray(formJson, "currencies", true);
        formJson.agregators = prepareJsonArray(formJson, "agregators", true);
        if (formJson.dateTimeFrom) formJson.dateTimeFrom = formatDate(formJson.dateTimeFrom);
        if (formJson.dateTimeTo) formJson.dateTimeTo = formatDate(formJson.dateTimeTo);
        return formJson;
    };

    const onMultiselectConfirm = (id, count, callback) => {
        setSelectedCounts(prev => ({ ...prev, [id]: count }));
        if (callback) callback();
        setTimeout(() => checkQualifyingFilters(), 1100);
    };

    const observer = useRef();
    const lastElementRef = useCallback((element) => {
        if (observer.current) observer.current.disconnect();
        if (!more) return;
        observer.current = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting && more) setPage((prev) => prev + 1);
        });
        if (element) observer.current.observe(element);
    }, [more]);

    const multiselectStyle = (id) => selectedCounts[id] > 0
        ? { borderColor: "#0d6efd" }
        : {};

    const getSavedValue = (id) => rawFormValues?.[id] || undefined;

    return (
        <>
            <div className="container">
                <div className="d-flex justify-content-between align-items-center">
                    <h3>Transactions</h3>
                </div>
                <div className="mt-2">
                    {filters === null ?
                        <div className="w-100 mt-4 d-flex justify-content-center">
                            <div className="spinner-border" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div> :
                        Object.keys(filters).length === 0 ? <p className="text-danger">Failed to load filters</p> :
                            <form key={clearKey} ref={formRef} className="row" method="post" onSubmit={onFilterFormSubmit}>
                                <div className="col-md-4">
                                    <UILabelInput inputType={inputType.TEXT} labelText={"BW Transaction ID"} disabled={false} required={false} inputId={"id"} onChange={checkQualifyingFilters}
                                        defaultValue={getSavedValue("id")} />
                                    <UILabelInput inputType={inputType.TEXT} labelText={"Game Session ID"} disabled={false} required={false} inputId={"gameSessionId"} onChange={checkQualifyingFilters}
                                        defaultValue={getSavedValue("gameSessionId")} />
                                    <UILabelInput inputType={inputType.TEXT} labelText={"Casino Session ID"} disabled={false} required={false} inputId={"operatorSessionId"} onChange={checkQualifyingFilters}
                                        defaultValue={getSavedValue("operatorSessionId")} />
                                    <UILabelInput inputType={inputType.TEXT} labelText={"Vendor Transaction ID"} disabled={false} required={false} inputId={"providerTransactionId"} onChange={checkQualifyingFilters}
                                        defaultValue={getSavedValue("providerTransactionId")} />
                                    <UILabelInput inputType={inputType.TEXT} labelText={"Round ID"} disabled={false} required={false} inputId={"roundId"} onChange={checkQualifyingFilters}
                                        defaultValue={getSavedValue("roundId")} />
                                    <UILabelInput inputType={inputType.NUMBER} labelText={"Player ID"} disabled={!hasQualifyingFilter} required={false} inputId={"playerId"} numberStep={"1"}
                                        onChange={checkQualifyingFilters}
                                        defaultValue={getSavedValue("playerId")} />
                                    <UILabelInput inputType={inputType.TEXT} labelText={"Player Operator ID"} disabled={!hasQualifyingFilter} required={false} inputId={"playerOperatorId"}
                                        onChange={checkQualifyingFilters}
                                        defaultValue={getSavedValue("playerOperatorId")} />
                                </div>
                                <div className="col-md-4">
                                    <div style={multiselectStyle("transactionType")}>
                                        <UIMultiselectModal title={"Transaction Types"} id={"transactionType"} options={filters.transactionTypes}
                                            disabled={!hasQualifyingFilter}
                                            savedValue={getSavedValue("transactionType")}
                                            onConfirm={(count) => onMultiselectConfirm("transactionType", count)} />
                                    </div>
                                    <div style={multiselectStyle("transactionStatus")}>
                                        <UIMultiselectModal title={"Transaction Status"} id={"transactionStatus"} options={filters.transactionStatus}
                                            disabled={!hasQualifyingFilter}
                                            savedValue={getSavedValue("transactionStatus")}
                                            onConfirm={(count) => onMultiselectConfirm("transactionStatus", count)} />
                                    </div>
                                    <div style={multiselectStyle("operators")}>
                                        <UIMultiselectModal title={"Operators"} id={"operators"} options={filters.operators} displayKey={"company_name"}
                                            disabled={!hasQualifyingFilter}
                                            savedValue={getSavedValue("operators")}
                                            onConfirm={(count) => onMultiselectConfirm("operators", count, onOperatorsSelected)} />
                                    </div>
                                    <div style={multiselectStyle("casinos")}>
                                        <UIMultiselectModal title={"Casinos"} id={"casinos"} options={casinos} displayKey={"company_name"}
                                            disabled={!hasQualifyingFilter}
                                            savedValue={getSavedValue("casinos")}
                                            onConfirm={(count) => onMultiselectConfirm("casinos", count, onCasinosSelected)} />
                                    </div>
                                    <div style={multiselectStyle("agregators")}>
                                        <UIMultiselectModal title={"Aggregators"} id={"agregators"} options={hideDuplicates(agregators)} displayKey={"name"}
                                            disabled={!hasQualifyingFilter}
                                            savedValue={getSavedValue("agregators")}
                                            onConfirm={(count) => onMultiselectConfirm("agregators", count, onAgregatorsSelected)} />
                                    </div>
                                    <div style={multiselectStyle("providers")}>
                                        <UIMultiselectModal title={"Vendors"} id={"providers"} options={hideDuplicates(providers)} displayKey={"name"}
                                            disabled={!hasQualifyingFilter}
                                            savedValue={getSavedValue("providers")}
                                            onConfirm={(count) => onMultiselectConfirm("providers", count, onProvidersSelected)} />
                                    </div>
                                    <div style={multiselectStyle("gameConfigs")}>
                                        <UIGameConfigFilter title={"Game Configs"} id={"gameConfigs"} displayKey={"name"}
                                            operatorId={"operators"} casinosId={"casinos"} agregatorsId={"agregators"} providersId={"providers"}
                                            setLoggedIn={props.setLoggedIn}
                                            disabled={!hasQualifyingFilter}
                                            savedValue={getSavedValue("gameConfigs")}
                                            savedOptions={savedGameConfigOptions}
                                            onConfirm={(count, options) => {
                                                onMultiselectConfirm("gameConfigs", count);
                                                setSavedGameConfigOptions(options);
                                            }} />
                                    </div>
                                    <div style={multiselectStyle("currencies")}>
                                        <UIMultiselectModal title={"Currencies"} id={"currencies"} options={filters.currencies} displayKey={"iso_code"}
                                            disabled={!hasQualifyingFilter}
                                            savedValue={getSavedValue("currencies")}
                                            onConfirm={(count) => onMultiselectConfirm("currencies", count)} />
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <UILabelInput inputType={inputType.DATETIME} labelText={"From"} disabled={false} required={false} inputId={"dateTimeFrom"}
                                        defaultValue={getSavedValue("dateTimeFrom")} />
                                    <UILabelInput inputType={inputType.DATETIME} labelText={"To"} disabled={false} required={false} inputId={"dateTimeTo"}
                                        defaultValue={getSavedValue("dateTimeTo")} />
                                    <div className="mt-2">
                                        <i>If no <strong>From</strong> and <strong>To</strong> date/time are selected, the last hour will be used by default.</i><br />
                                        <i><strong>Please select at least one ID field (BW Transaction ID, Game Session ID, Casino Session ID, Vendor Transaction ID, or Round ID) before using any other filter.</strong></i>
                                    </div>
                                    <div className="clearFilter mt-2">
                                        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onClearFilters}>Clear All Filters</button>
                                    </div>
                                </div>
                                <div className="col-md-12 mt-2">
                                    <UIButton buttonType={"submit"} buttonText={"SEARCH"} styleType={styleType.FADEBLACK} additionalClass="w-100" />
                                </div>
                            </form>
                    }
                </div>
                <div className='mt-3'>
                    <UITable
                        columnTitles={["ID", "Amount", "Game Session", "Game Config", "Currency", "Status", "Type", "Round ID", "Vendor Transaction ID", "Created At"]}
                        data={data}
                        objectKeys={["id", "amount", "game_session.session", "game_session.operator_game_config.game_config.name", "game_session.player_currency.currency.iso_code", "status", "type", "round_id", "provider_transaction_id", "created_at"]}
                        primaryKey={'id'}
                        lastElementRef={lastElementRef}
                        detailsHref={"/transaction/details"}
                        preventDelete={true}
                        tableClass="transaction-table"
                    />
                    {loading & (more || page === 1) ?
                        <div className="d-flex justify-content-center">
                            <div className="spinner-border" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div> : <span></span>}
                    {!more && !loading && <p className='text-center p-4 text-secondary font-weight-bold'>No more data to be loaded</p>}
                </div>
            </div>
        </>
    );
}

export default TransactionListPage;